import React, {useEffect, useRef, useState} from 'react';
import {useParams, useSearchParams} from "react-router";
import {useQuery} from "@tanstack/react-query";
import {format, isValid, parse} from "date-fns";
import {FaExclamationTriangle, FaExternalLinkAlt, FaPlus} from "react-icons/fa";
import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import Button from "../../../../components/button/button.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import InputDatetime from "../../../../components/input/input-datetime.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import InputTextarea from "../../../../components/input/input-textarea.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import {MaskDefault} from "../../../../utils/mask.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import projectUtils from "../../../../utils/project-utils.js";
import IndividualMonitoringService from "../../../../services/web/individual-monitoring-service.js";
import IndividualMonitoringWatchlistService from "../../../../services/web/individual-monitoring-watchlist-service.js";
import MonitoringFaceImage from "./components/monitoring-face-image.jsx";
import {clearCpf, errMessageI18N, getAttachUrl, USER_IMAGE} from "../utils/individual-monitoring-utils.js";
import Alert from "../../../../components/alert/alert.jsx";
import SentryRow from "../../../../components/template/sentry-row.jsx";
import InputSwitch from "../../../../components/input/input-switch.jsx";

const API_URL = import.meta.env.VITE_API_URL;
const MAX_IMAGE_SIZE = 2000000;
const ACCEPTED_TYPES = ['jpg', 'jpeg', 'image/jpg', 'image/jpeg'];

const initialForm = {
    status: true,
    cpf: "",
    withoutCpf: false,
    name: "",
    dtBirth: null,
    description: "",
};

const parseYmd = (value) => {
    if (!value) return null;
    const parsed = parse(String(value).slice(0, 10), "yyyy-MM-dd", new Date());
    return isValid(parsed) ? parsed : null;
};

const normalizeYmd = (value) => value ? String(value).slice(0, 10) : null;

const mapWatchlistOptions = (raw) => (raw ?? []).map(option => ({
    value: String(option.value ?? option.id),
    label: option.label ?? option.name ?? option.text
}));

const IndividualMonitoringForm = () => {
    const {id} = useParams();
    const [searchParams] = useSearchParams();
    const navigate = navigateHook();

    const [form, setForm] = useState(initialForm);
    const [watchlists, setWatchlists] = useState([]);
    const [image, setImage] = useState(undefined);
    const [imageId, setImageId] = useState(null);
    const [imagePreview, setImagePreview] = useState(USER_IMAGE);
    const [individualData, setIndividualData] = useState(undefined);
    const [saving, setSaving] = useState(false);

    const initialized = useRef(false);
    const watchlistsInitialized = useRef(false);
    const cpfOnFocus = useRef("");

    const {data: status} = useQuery({
        queryFn: () => IndividualMonitoringService.status(),
        queryKey: ["facial-recognition-status"]
    });

    const {data: watchlistOptionsRaw} = useQuery({
        queryFn: () => IndividualMonitoringWatchlistService.getOptions(),
        queryKey: ["individual-monitoring-watchlist-options"]
    });

    const {data: monitoring, isLoading: isLoadingMonitoring, refetch} = useQuery({
        queryFn: () => IndividualMonitoringService.get(id),
        queryKey: ["individual-monitoring", id],
        enabled: !!id,
        staleTime: 0
    });

    const watchlistOptions = mapWatchlistOptions(watchlistOptionsRaw);

    const facialDisabled = status === false;

    const showFaceImage = (attachId) => setImagePreview(attachId ? getAttachUrl(attachId) : USER_IMAGE);

    const handleIndividualData = async (individual) => {
        const {cpf, name, dtBirth, relatedPhotoFaceId} = individual;

        setForm(prev => ({
            ...prev,
            cpf: cpf ? projectUtils.formatCpfToView(cpf) : "",
            withoutCpf: !cpf ? true : prev.withoutCpf,
            name: name ?? "",
            dtBirth: parseYmd(dtBirth),
        }));

        if (relatedPhotoFaceId) {
            showFaceImage(relatedPhotoFaceId);
            setImageId(relatedPhotoFaceId);

            const response = await fetch(getAttachUrl(relatedPhotoFaceId));
            const blob = await response.blob();
            setImage(new File([blob], "image.jpg", {type: "image/jpeg"}));
        }
    };

    const individualByCPF = async (cpf, insert = true, showAlert = false) => {
        if (!cpf) {
            setIndividualData(undefined);
            return;
        }

        let individual;
        try {
            individual = await IndividualMonitoringService.getIndividualByCpf(clearCpf(cpf));
        } catch {
            setIndividualData(undefined);
            return;
        }

        if (!individual) return;

        setIndividualData({name: individual.name, dtBirth: normalizeYmd(individual.dtBirth)});

        if (!insert) return;

        const hasCurrentData =
            (form.name && form.name !== individual.name) ||
            (form.dtBirth && format(form.dtBirth, "yyyy-MM-dd") !== normalizeYmd(individual.dtBirth)) ||
            imageId || image;

        if (showAlert && hasCurrentData) {
            projectUtils.swalMessage(
                "warning",
                "CPF já está no sistema",
                "Este CPF já está vinculado a um cadastro de Indivíduo. Deseja sobrescrever os dados atuais?",
                'Sim',
                true,
                'Não',
                ({isConfirmed}) => isConfirmed && handleIndividualData(individual)
            );
            return;
        }

        await handleIndividualData(individual);
    };

    useEffect(() => {
        if (initialized.current) return;

        const cpfParam = searchParams.get("cpf");
        const dataParam = searchParams.get("data");

        if (cpfParam) {
            initialized.current = true;
            individualByCPF(cpfParam, true);
            return;
        }

        if (dataParam) {
            initialized.current = true;
            const individual = JSON.parse(atob(dataParam));
            handleIndividualData(individual).then(() => individualByCPF(individual.cpf, false));
            return;
        }
    }, [searchParams]);

    useEffect(() => {
        if (!id || !monitoring || initialized.current) return;
        initialized.current = true;

        handleIndividualData(monitoring);

        setForm(prev => ({...prev, status: !!monitoring.status, description: monitoring.description ?? ""}));

        showFaceImage(monitoring.imageId);
        setImageId(monitoring.imageId ?? null);

        individualByCPF(monitoring.cpf, false);
    }, [id, monitoring]);

    useEffect(() => {
        if (!id || !monitoring || !watchlistOptionsRaw || watchlistsInitialized.current) return;
        watchlistsInitialized.current = true;

        const ids = (monitoring.watchlists ?? []).map(item => String(item.id));
        setWatchlists(mapWatchlistOptions(watchlistOptionsRaw).filter(option => ids.includes(String(option.value))));
    }, [id, monitoring, watchlistOptionsRaw]);

    const onChange = ({target: {id: field, value}}) => setForm(prev => ({...prev, [field]: value}));

    const onCpfFocus = () => {
        cpfOnFocus.current = form.cpf;
    };

    const onCpfBlur = () => {
        if (form.cpf === cpfOnFocus.current) return;
        if (form.cpf && !form.withoutCpf) individualByCPF(form.cpf, true, true);
    };

    const onWithoutCpfChange = ({target: {checked}}) => {
        setForm(prev => ({...prev, withoutCpf: checked, cpf: checked ? "" : prev.cpf}));
    };

    const attachImage = (file) => {
        if (!file) return;

        if (file.size > MAX_IMAGE_SIZE) {
            projectUtils.swalMessage('warning', '', `Tamanho máximo de arquivo excedido. Limite: 2MB`, 'Ok');
            return;
        }

        if (!ACCEPTED_TYPES.includes(file.type)) {
            projectUtils.swalErrorMessage("", errMessageI18N("facegateway.image.type.unsupported"), "Ok");
            return;
        }

        const reader = new FileReader();
        reader.onload = ({target: {result}}) => setImagePreview(result);
        reader.readAsDataURL(file);

        setImage(file);
        setImageId(null);
    };

    const removeImage = () => {
        setImage(undefined);
        setImageId(null);
        setImagePreview(USER_IMAGE);
    };

    const openIndividual = () => {
        const cpf = clearCpf(form.cpf);
        navigate(`/individuals/form/${cpf}`, true);
    };

    const submit = (individual) => {
        setSaving(true);

        const formData = new FormData();
        formData.append("individual", JSON.stringify(individual));
        if (image) formData.append("file", image);

        const response = id ? IndividualMonitoringService.update(formData) : IndividualMonitoringService.create(formData);

        response
            .then((data) => {
                projectUtils.success(!id);

                if (id) {
                    setImage(undefined);
                    initialized.current = false;
                    watchlistsInitialized.current = false;
                    refetch();
                    setSaving(false);
                    return;
                }

                navigate(`/individual-monitoring/form/${data}`);
            })
            .catch(error => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response?.status !== 500) message = errMessageI18N(error.response?.data?.message ?? error.response?.data);

                setSaving(false);
                projectUtils.swalErrorMessage('', message, 'Ok');
            });
    };

    const save = () => {
        if ((!image && !imageId) && (!form.cpf || clearCpf(form.cpf).length !== 11)) {
            projectUtils.swalErrorMessage('', "Insira uma foto ou um CPF antes de salvar.", 'Ok');
            return;
        }

        const individual = {
            id,
            status: form.status,
            cpf: form.cpf ? clearCpf(form.cpf) : null,
            name: form.name,
            dtBirth: form.dtBirth ? format(form.dtBirth, "yyyy-MM-dd") : null,
            description: form.description || null,
            imageId: imageId ?? "",
            watchlists: watchlists.map(item => item.value),
        };

        const dtBirthChanged = individualData && normalizeYmd(individual.dtBirth) !== individualData.dtBirth;
        const nameChanged = individualData && form.name !== individualData.name;

        if (individualData && (nameChanged || dtBirthChanged)) {
            projectUtils.swalMessage(
                'warning',
                'Alterar o indivíduo',
                'Deseja sobrescrever os dados do indivíduo?',
                'Sim',
                true,
                'Não',
                ({isConfirmed}) => submit(isConfirmed ? {...individual, changeIndividual: true} : individual)
            );
            return;
        }

        submit(individual);
    };

    const handleDelete = () => {
        projectUtils.swalMessage(
            'warning',
            'Excluir',
            'Excluir monitoramento?',
            'Sim',
            true,
            'Não',
            ({isConfirmed}) => {
                if (!isConfirmed) return;

                IndividualMonitoringService.delete(id)
                    .then(() => {
                        projectUtils.success(true, 'Excluído com sucesso');
                        navigate("/individual-monitoring");
                    })
                    .catch(error => {
                        console.error(error);
                        let message = 'Não foi possível salvar.';
                        if (error.response?.status !== 500) message = errMessageI18N(error.response?.data?.message, "Não foi possível excluir.");
                        projectUtils.swalErrorMessage('', message, 'Ok');
                    });
            }
        );
    };

    return (
        <Template isLoading={!!id && isLoadingMonitoring}>
            <Template.Header>
                <Template.Title browserTitle={'Monitoramento'}>Monitoramento</Template.Title>

                {
                    id &&
                    <Button target={"primary-outline"} onClick={() => navigate("/individual-monitoring/form")}>
                        <FaPlus/> Adicionar monitoramento
                    </Button>
                }
            </Template.Header>

            <Template.Body className={"space-y-2"}>
                <Card>
                    <Card.Header>
                        <Card.Title>Geral</Card.Title>
                    </Card.Header>
                    <Card.Body className={"space-y-3"}>
                        <SentryRow>
                            <div className={"col-span-12"}>
                                <InputSwitch
                                    id="status"
                                    label="Monitoramento ativo"
                                    checked={form.status}
                                    onChange={(value) => setForm(prev => ({...prev, status: value}))}
                                />

                                {
                                    facialDisabled &&
                                    <Alert target={"danger"} className={"col-span-3 flex items-center gap-2"}>
                                        <FaExclamationTriangle /> Sistema de reconhecimento facial não configurado.
                                    </Alert>
                                }
                            </div>
                        </SentryRow>

                        <SentryRow>
                            <SentryRow className={"col-span-9"}>
                                <div className={"col-span-8 flex gap-2"}>
                                    <InputForm
                                        classNameWrapper={"w-full"}
                                        id={"cpf"}
                                        label={"CPF"}
                                        value={form.cpf}
                                        onChange={onChange}
                                        onFocus={onCpfFocus}
                                        onBlur={onCpfBlur}
                                        disabled={form.withoutCpf}
                                        mask={MaskDefault.cpf}
                                    />
                                    <Button target={"primary"} title={"Abrir indivíduo"} disabled={!individualData} onClick={openIndividual}>
                                        <FaExternalLinkAlt/>
                                    </Button>
                                </div>

                                <div className={"col-span-4 flex items-center"}>
                                    <InputCheckbox
                                        id={"without-cpf"}
                                        label={"Sem CPF"}
                                        checked={form.withoutCpf}
                                        disabled={!!clearCpf(form.cpf)}
                                        onChange={onWithoutCpfChange}
                                    />
                                </div>

                                <div className={"col-span-8"}>
                                    <InputForm id={"name"} label={"Nome"} value={form.name} onChange={onChange}/>
                                </div>

                                <div className={"col-span-4"}>
                                    <InputDatetime
                                        id={"dtBirth"}
                                        label={"Data de nascimento"}
                                        dateFormat={"L"}
                                        selected={form.dtBirth}
                                        onChange={(date) => setForm(prev => ({...prev, dtBirth: date}))}
                                    />
                                </div>

                                <div className={"col-span-12"}>
                                    <SelectComponent
                                        value={watchlists}
                                        options={watchlistOptions}
                                        label={"Lista de monitoramento *"}
                                        onChange={(value) => setWatchlists(value ?? [])}
                                        isMulti
                                    />
                                </div>

                                <div className={"col-span-12"}>
                                    <InputTextarea
                                        id={"description"}
                                        label={"Observações"}
                                        value={form.description}
                                        onChange={onChange}
                                        textAreaClass={"min-h-[105px]"}
                                    />
                                </div>
                            </SentryRow>

                            <div className={"col-span-3"}>
                                <MonitoringFaceImage
                                    preview={imagePreview}
                                    image={image}
                                    hasImage={!!(image || imageId)}
                                    disabled={facialDisabled}
                                    onFileSelected={attachImage}
                                    onRemove={removeImage}
                                />
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body className={"flex justify-end gap-2"}>
                        {
                            id &&
                            <Button target={"danger"} onClick={handleDelete} actionFeedbackType='delete'>Excluir</Button>
                        }
                        <Button target={"primary"} onClick={save} loading={saving} actionFeedbackType='save'>Salvar</Button>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default IndividualMonitoringForm;